import { Typography, Stack, Box } from '@mui/material';
import React, { ReactElement } from 'react';

import { theme } from '@app/theme';
import { Product } from '@app/types/catalog';
import {
  formatPrice,
  formatPriceRange,
  getTransportMeanLabel,
  getTransportTimeLabel,
} from '@app/utils/format';

interface PropertySalesInfoProps {
  product: Product;
}

export function PropertySalesInfo({
  product,
}: PropertySalesInfoProps): ReactElement {
  return (
    <>
      <Typography
        bgcolor={theme.palette.blueAccent.main}
        color="white"
        px={2}
        height={36}
        fontWeight={600}
        borderRadius={15}
        display="flex"
        alignItems="center"
        variant="h6"
      >
        販売概要
      </Typography>

      <Typography
        color={theme.palette.blueAccent.main}
        fontSize="clamp(1.25rem, 0.9112rem + 1.39vw, 1.875rem)"
        fontWeight={500}
        borderBottom={1}
        borderColor={theme.palette.blueAccent.main}
        pt={3}
        pb={2}
      >
        {formatPriceRange(
          product.customFields.minPrice
            ? Number(product.customFields.minPrice)
            : undefined,
          product.customFields.maxPrice
            ? Number(product.customFields.maxPrice)
            : undefined,
          formatPrice,
          product.customFields.priceRangeType
        )}
      </Typography>
      <Stack direction="row" gap={3} borderBottom="2px dotted #E0E0E0" py={1.5}>
        <Box sx={{ flexShrink: 0, width: 65 }}>
          <Typography color={theme.palette.blueAccent.main}>交通</Typography>
        </Box>
        <Stack flexGrow={1}>
          {product.customFields.transportMainLine ||
          product.customFields.transportSub1Line ||
          product.customFields.transportSub2Line ? (
            <>
              {product.customFields.transportMainLine && (
                <Typography variant="body2">
                  {product.customFields.transportMainLine}「
                  {product.customFields.transportMainNearest}」まで
                  {getTransportMeanLabel(
                    product.customFields.transportMainMean
                  )}
                  {getTransportTimeLabel(
                    product.customFields.transportMainMean,
                    product.customFields.transportMainTimeMin,
                    product.customFields.transportMainTimeMax,
                    product.customFields.transportMainNote
                  )}
                </Typography>
              )}
              {product.customFields.transportSub1Line && (
                <Typography variant="body2">
                  {product.customFields.transportSub1Line}「
                  {product.customFields.transportSub1Nearest}」まで
                  {getTransportMeanLabel(
                    product.customFields.transportSub1Mean
                  )}
                  {getTransportTimeLabel(
                    product.customFields.transportSub1Mean,
                    product.customFields.transportSub1TimeMin,
                    product.customFields.transportSub1TimeMax,
                    product.customFields.transportSub1Note
                  )}
                </Typography>
              )}
              {product.customFields.transportSub2Line && (
                <Typography variant="body2">
                  {product.customFields.transportSub2Line}「
                  {product.customFields.transportSub2Nearest}」まで
                  {getTransportMeanLabel(
                    product.customFields.transportSub2Mean
                  )}
                  {getTransportTimeLabel(
                    product.customFields.transportSub2Mean,
                    product.customFields.transportSub2TimeMin,
                    product.customFields.transportSub2TimeMax,
                    product.customFields.transportSub2Note
                  )}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2">-</Typography>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" gap={3} borderBottom="2px dotted #E0E0E0" py={1.5}>
        <Box sx={{ flexShrink: 0, width: 65 }}>
          <Typography color={theme.palette.blueAccent.main}>
            販売戸数
          </Typography>
        </Box>
        <Typography variant="body2" flexGrow={1}>
          {product.customFields.salesHouseCount
            ? `${product.customFields.salesHouseCount}戸`
            : '未定'}
        </Typography>
      </Stack>

      <Stack direction="row" gap={3} borderBottom="2px dotted #E0E0E0" py={1.5}>
        <Box sx={{ flexShrink: 0, width: 65 }}>
          <Typography color={theme.palette.blueAccent.main}>住所</Typography>
        </Box>
        <Typography variant="body2" flexGrow={1}>
          {product.addressLine1 + product.addressLine2}
          {product.customFields.addressLine3HiddenFlag === true &&
            product.addressLine3}
        </Typography>
      </Stack>
    </>
  );
}
